import type { ImgHTMLAttributes } from 'react';

import { IMAGE_BASE } from '../constants';

interface PictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'width' | 'height'> {
  /** File stem under /assets/images/trial/v4a, without the width suffix: `logo`, `player-sacy-face`. */
  name: string;
  /** Widths shipped for this stem (`logo-256`, `logo-512`…). Empty when the stem already carries it. */
  widths?: readonly number[];
  /** Width of the WebP the `<img src>` falls back to (defaults to the largest). */
  fallbackWidth?: number;
  width: number;
  height: number;
  alt: string;
}

function file(name: string, width: number | undefined, extension: 'avif' | 'webp'): string {
  return `${IMAGE_BASE}/${name}${width ? `-${width}` : ''}.${extension}`;
}

function srcSetOf(name: string, widths: readonly number[], extension: 'avif' | 'webp'): string {
  if (!widths.length) return file(name, undefined, extension);
  return widths.map((width) => `${file(name, width, extension)} ${width}w`).join(', ');
}

/**
 * AVIF first, WebP for everything else. The design's `<picture>` is layout-transparent (display: contents,
 * set in the module's base layer), so the `<img>` stays the flex/grid item exactly as a bare image would.
 */
export function Picture({ name, widths = [], fallbackWidth, sizes, alt, width, height, decoding = 'async', ...imgProps }: PictureProps) {
  const fallback = fallbackWidth ?? widths[widths.length - 1];
  return (
    <picture>
      <source type='image/avif' srcSet={srcSetOf(name, widths, 'avif')} sizes={widths.length ? sizes : undefined} />
      <img src={file(name, fallback, 'webp')} srcSet={srcSetOf(name, widths, 'webp')} sizes={widths.length ? sizes : undefined} alt={alt} width={width} height={height} decoding={decoding} {...imgProps} />
    </picture>
  );
}
